/**
 * Light or dark. The page is light by design and dark by the same design
 * read by lamplight: the two are one set of tokens with two sets of
 * values (styles/tokens.css), chosen by `data-theme` on the root element.
 * Until someone chooses, it is whatever their device is set to; once they
 * have, that is kept in this browser.
 */
export const THEMES = ["light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

const KEY = "theme";

/** What was chosen in this browser, or null if nothing has been. */
export function chosenTheme(storage: Pick<Storage, "getItem"> | null): Theme | null {
  try {
    const kept = storage?.getItem(KEY);
    return kept === "light" || kept === "dark" ? kept : null;
  } catch {
    return null;
  }
}

/** The theme to show: the one chosen, or failing that the device's. */
export function themeFor(chosen: Theme | null, deviceIsDark: boolean): Theme {
  return chosen ?? (deviceIsDark ? "dark" : "light");
}

/** The theme the page is showing now. */
export function currentTheme(): Theme {
  return document.documentElement.dataset["theme"] === "dark" ? "dark" : "light";
}

/** Show a theme, and keep it as this browser's choice. */
export function chooseTheme(theme: Theme): void {
  document.documentElement.dataset["theme"] = theme;
  try {
    window.localStorage.setItem(KEY, theme);
  } catch {
    // Not kept: it holds for this visit, and the device decides again next time.
  }
}
